// ---------------------------------------------------------------------------
// Sound — tiny procedural WebAudio effects, no asset files.
//
// Every effect is synthesized (filtered noise bursts, short sines), so the
// bundle stays asset-free and the sounds are latency-free. The AudioContext
// can only start after a user gesture; init() hooks the first pointer/key
// event. Volume + mute come from Settings.
// ---------------------------------------------------------------------------

import { getDef } from "../shared/defs";

export type SfxKind =
  | "flip"      // card flick
  | "drop"      // object set down
  | "stack"     // card/chip lands on a pile
  | "draw"      // card swish (draw/deal to a hand)
  | "shuffle"   // riffle
  | "roll"      // dice rattle
  | "deal";     // dealing swish burst

/** What an object sounds like when it hits the table or a pile. */
export type SfxMaterial = "card" | "chip" | "die" | "wood";

/** Material for a def id: cards whisper, chips clack, dice click, wood knocks. */
export function materialOf(defId: string): SfxMaterial {
  const d = getDef(defId);
  if (d.kind === "die") return "die";
  const group = d.stackGroup ?? d.acceptsGroup ?? "";
  if (group === "cards.domino") return "chip"; // bone tiles clack, not whisper
  if (d.kind === "card" || group.startsWith("cards")) return "card";
  if (d.piece || group.startsWith("checkers") || d.kind === "board") return "wood";
  return "chip"; // chips, damage counters, generic tokens: hard plastic
}

export class Sound {
  private ctx: AudioContext | null = null;
  private noise: AudioBuffer | null = null;
  private lastPlayed = new Map<string, number>();

  constructor(private prefs: () => { on: boolean; volume: number }) {}

  /** Call once; arms creation of the AudioContext on the first user gesture. */
  init(): void {
    const arm = () => {
      this.ensure();
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("keydown", arm);
    };
    window.addEventListener("pointerdown", arm);
    window.addEventListener("keydown", arm);
  }

  private ensure(): AudioContext | null {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return null;
      }
      // 1s of white noise, reused by every noise-based effect.
      const len = this.ctx.sampleRate;
      this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    return this.ctx;
  }

  play(kind: SfxKind, material: SfxMaterial = "card"): void {
    const p = this.prefs();
    if (!p.on || p.volume <= 0) return;
    const ctx = this.ensure();
    if (!ctx || ctx.state !== "running" || !this.noise) return;
    // Burst dedup: a 7-card deal is one swish, not seven. Keyed per material
    // so a chip clack doesn't swallow a simultaneous card drop.
    const now = performance.now();
    const dedupKey = `${kind}:${material}`;
    if (now - (this.lastPlayed.get(dedupKey) ?? -1e9) < 90) return;
    this.lastPlayed.set(dedupKey, now);

    const t = ctx.currentTime;
    const vol = p.volume;
    switch (kind) {
      case "flip":
        this.swish(t, 0.05, 1800, 3500, 0.5 * vol);
        break;
      case "draw":
      case "deal":
        this.swish(t, 0.12, 500, 2600, 0.45 * vol);
        break;
      case "drop":
        switch (material) {
          case "chip": // plastic clack: sharp click + short body
            this.tick(t, 3200, 0.018, 0.45 * vol);
            this.thud(t + 0.006, 340, 0.05, 0.3 * vol);
            break;
          case "die": // hard click on the felt
            this.tick(t, 2900, 0.02, 0.4 * vol);
            this.thud(t + 0.008, 260, 0.05, 0.32 * vol);
            break;
          case "wood": // piece/board knock: low and round
            this.thud(t, 120, 0.1, 0.55 * vol);
            this.tick(t + 0.004, 750, 0.03, 0.28 * vol);
            break;
          default: // card: the original soft thud
            this.thud(t, 150, 0.09, 0.5 * vol);
        }
        break;
      case "stack":
        switch (material) {
          case "chip": // bright double clack, chips kissing
            this.tick(t, 3400, 0.018, 0.5 * vol);
            this.tick(t + 0.025, 2700, 0.02, 0.36 * vol);
            break;
          case "die": // one sharp click
            this.tick(t, 3000, 0.02, 0.42 * vol);
            break;
          case "wood": // kinging a checker: knock + wooden tap
            this.thud(t, 240, 0.06, 0.4 * vol);
            this.tick(t + 0.02, 950, 0.03, 0.3 * vol);
            break;
          default: // card on a pile: gentle paper ticks
            this.tick(t, 2600, 0.02, 0.4 * vol);
            this.tick(t + 0.03, 1900, 0.025, 0.3 * vol);
        }
        break;
      case "shuffle": {
        // Riffle: a run of accelerating ticks.
        for (let i = 0; i < 14; i++) {
          const dt = i * (0.016 + i * 0.0012) + Math.random() * 0.006;
          this.tick(t + dt, 1400 + Math.random() * 1800, 0.014, 0.22 * vol);
        }
        break;
      }
      case "roll": {
        // Dice rattle: sparse clicks slowing to a final landing tick.
        const hits = [0, 0.07, 0.16, 0.28, 0.42, 0.6, 0.82];
        hits.forEach((dt, i) => {
          this.tick(t + dt + Math.random() * 0.02, 2800 - i * 220, 0.02, (0.32 - i * 0.03) * vol);
        });
        this.thud(t + 0.88, 220, 0.06, 0.35 * vol);
        break;
      }
    }
  }

  /** Band-passed noise sweep — card swishes and flicks. */
  private swish(t: number, dur: number, f0: number, f1: number, gain: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise!;
    src.playbackRate.value = 1;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 1.1;
    bp.frequency.setValueAtTime(f0, t);
    bp.frequency.exponentialRampToValueAtTime(f1, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + dur * 0.25);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start(t, Math.random() * 0.5, dur + 0.05);
  }

  /** Very short high-passed noise click — riffles, chip clacks, dice taps. */
  private tick(t: number, freq: number, dur: number, gain: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise!;
    const hp = ctx.createBiquadFilter();
    hp.type = "bandpass";
    hp.Q.value = 2.5;
    hp.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(hp).connect(g).connect(ctx.destination);
    src.start(t, Math.random() * 0.5, dur + 0.02);
  }

  /** Low sine drop with a fast pitch fall — things set down on the table. */
  private thud(t: number, f0: number, dur: number, gain: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(f0, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, f0 * 0.4), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }
}
